import CardWrapper from '../../../components/wrapper/card';
import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Typography
} from '@mui/material';
import {
  Email as EmailIcon,
  Person as PersonIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';

export interface LeadObject {
  id: number;
  form_id: number;
  form_data: string;
  email: string | null;
  phone: string | null;
  type: 'internal' | 'external' | 'snapps' | 'elementor' | 'custom' | 'snapps-manual' | 'elementor-manual';
  lead_external_id: string | null;
  created_at: string;
  updated_at: string;
  source: string;
  form_title: string;
}

interface RecentLeadsProps {
  leads: LeadObject[];
  maxHeight?: string;
}

const getLeadTypeChipColor = (type: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (type) {
    case 'internal': return 'success';
    case 'external': return 'primary';
    case 'snapps': return 'warning';
    case 'elementor': return 'secondary';
    case 'custom': return 'error';
    default: return 'default';
  }
};

const RecentLeads: React.FC<RecentLeadsProps> = ({ leads, maxHeight = '400px' }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseFormData = (formData: string) => {
    try {
      const parsed = JSON.parse(formData);
      return parsed.name || parsed.full_name || parsed.firstName || 'Unknown Lead';
    } catch {
      return 'Unknown Lead';
    }
  };

  return (
    <CardWrapper >
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Incoming Leads
      </Typography>
      <Box sx={{ maxHeight, overflowY: 'auto', overflowX: 'hidden' }}>
        {leads?.length === 0
        ? (
          <Box display="flex" alignItems="center" justifyContent="center" height="200px">
            <Typography variant="body2" color="text.secondary">
              No recent leads found
            </Typography>
          </Box>
        )
        : (
          <List sx={{ width: '100%' }}>
            {leads?.map((lead) => (
              <ListItem key={lead.id} alignItems="flex-start" sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'lightgray' }}>
                    <PersonIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={lead.email}
                  secondary={
                    <Box>
                      {lead.phone && (
                        <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {lead.phone}
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Form Title:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(lead.created_at)}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Chip
                    label={lead.form_title}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </CardWrapper>
  );
};

export default RecentLeads;
